import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { LuFileText, LuPhone, LuLaptop, LuUserCheck, LuHandshake, LuSprout, LuTarget, LuMessageSquare, LuWrench, LuTrendingUp } from "react-icons/lu";
import { useNavigate } from "react-router";
import { Images } from "../controller/Images";
import { GlobalStateContext } from "../controller/GlobalState";

// Same trust-logo set shown on /generative-ai-development and /about.
const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

const APPROACH = [
  {
    num: "01 / SCREEN",
    title: "We screen for fit, not just keywords.",
    copy: "Every CV is reviewed against the actual role, followed by a short call to talk through your experience and answer your questions.",
    tags: ["CV review", "Phone screening", "Real feedback"],
  },
  {
    num: "02 / PROVE",
    title: "You show real work, not a whiteboard trick.",
    copy: "An on-site skills and knowledge assessment relevant to the role you've applied for, so you can demonstrate real ability.",
    tags: ["Skills assessment", "Role-relevant tasks", "Two-way conversation"],
  },
  {
    num: "03 / DECIDE",
    title: "A final conversation, not a black box.",
    copy: "A final interview with our CEO and HR executive — the last step before an offer, and a chance for you to ask us anything.",
    tags: ["CEO interview", "HR executive", "Fast decision"],
  },
];

// Real recruitment stages — the same four steps described on /career.
const HIRING_STEPS = [
  { n: "01", t: "CV Submission", d: "Submit your CV through our online portal — every application is reviewed against the role's actual requirements." },
  { n: "02", t: "Phone Screening", d: "A short call to talk through your experience and what you're looking for, and for us to answer questions about the role." },
  { n: "03", t: "Skill Assessment", d: "An on-site skills and knowledge assessment relevant to the role you've applied for, so you can show real work." },
  { n: "04", t: "Final Interview", d: "A final conversation with our CEO and HR executive — the last step before an offer." },
];

const LOOK_FOR_CRITERIA = [
  { a: "Do you take ownership?", b: "OWNERSHIP" },
  { a: "Can you communicate directly?", b: "CLARITY" },
  { a: "Do you care about the craft?", b: "CRAFT" },
  { a: "Will you grow with us?", b: "GROWTH" },
];

const QUALITIES = [
  { Icon: LuHandshake, title: "Collaboration over hierarchy", copy: "Every team member plays a vital role in our success — not just senior leadership.", featured: true },
  { Icon: LuTarget, title: "Ownership", copy: "You follow through on your work without being chased for updates." },
  { Icon: LuMessageSquare, title: "Direct communication", copy: "Say what you mean, kindly and clearly — no politics, no guessing games." },
  { Icon: LuWrench, title: "Craft", copy: "You care about how something is built, not just whether it ships on time." },
  { Icon: LuSprout, title: "Growth mindset", copy: "As we grow, we welcome people who are driven to grow alongside us." },
  { Icon: LuTrendingUp, title: "Every role matters", copy: "We believe great software is built by a whole team, not a single hero." },
];

const FACTS = [
  { a: "4", b: "Hiring stages" },
  { a: "3", b: "Culture principles" },
  { a: "0", b: "Stages skipped" },
  { a: "1", b: "Same process, every role" },
];

const HIRING_RESULTS = [
  { a: "CV Submission", b: "Stage 1" },
  { a: "Phone Screening", b: "Stage 2" },
  { a: "Skill Assessment", b: "Stage 3" },
  { a: "Final Interview", b: "Stage 4" },
];

const WHO_THRIVES_HERE = [
  { a: "Builders who ship", b: "People who'd rather write code than talk about writing code." },
  { a: "Direct communicators", b: "We skip the politics — say what you mean, kindly and clearly." },
  { a: "Craft-focused engineers", b: "You care about code quality, not just the deadline." },
  { a: "Collaborative problem-solvers", b: "Great software is built together, not by a lone hero." },
  { a: "Fast learners", b: "Our stack and clients change — comfort with new tools matters." },
  { a: "Ownership-minded", b: "You follow through without being chased for updates." },
  { a: "Calm under deadlines", b: "Real projects have real pressure — we look for steady hands." },
  { a: "Curious about AI", b: "Generative AI and automation are part of daily work here." },
];

const FAQS = [
  {
    q: "How do I apply for a job at InQmise?",
    a: "Submit your CV through our Career page. From there, our process is four stages: CV submission, a phone screening, an on-site skill assessment, and a final interview with our CEO and HR executive.",
  },
  {
    q: "What does InQmise look for in candidates?",
    a: "People who value collaboration over hierarchy, take ownership of their role, and care about clean, maintainable work.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

// Same section-for-section structure as /generative-ai-development and
// /about, applied here with real hiring-process and culture content — no
// invented team bios, headshots, or stock photography.
const OurCoreTeam = () => {
  const navigate = useNavigate();
  const { setIsJobFormOpen } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "Our Core Team | InQmise Technologies";

    const description = document.createElement("meta");
    description.name = "description";
    description.content = "Meet the culture behind InQmise Technologies — a team that values collaboration, creativity, and the pursuit of excellence.";
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/our-core-team");

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
  }, []);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">The people behind the work · Careers at InQmise</p>
          <h1>
            A team that treats <em>your success</em> as its own.
          </h1>
          <p className="ea-lede">
            We are looking for talented and passionate individuals who are eager to contribute, grow, and make an impact. If you're ready to embark on an exciting journey with us, we'd love to hear from you.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={() => navigate("/career")} aria-label="View open roles">
              View open roles ↗
            </button>
            <a className="ea-text-link" href="#hiring">See how we hire</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / TEAM PROFILE <b>LIVE</b></div>
          <h2>Collaboration over hierarchy, by design.</h2>
          <div className="ea-query"><small>CULTURE</small>Every team member plays a vital role — not just senior leadership.</div>
          <div className="ea-query"><small>HIRING</small>Four transparent stages, the same for every role.</div>
          <div className="ea-flow"><b>Apply</b><i>→</i><b>Interview</b><i>→</i><b>Join</b></div>
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* HOW WE HIRE — feature rows */}
      <section className="ea-wrap ea-section" id="hiring">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">How we hire</div>
            <h2>A process built<br />on transparency.</h2>
          </div>
          <p>The same standards, and the same steps, regardless of the role.</p>
        </Reveal>

        {APPROACH.map((item, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
            <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </Reveal>
        ))}
      </section>

      {/* THE FOUR STAGES — process grid */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">The hiring stages</div>
            <h2>A straightforward path<br />to joining the team.</h2>
          </div>
          <p>Four stages, no surprises — from your first CV to meeting our CEO.</p>
        </Reveal>
        <div className="ea-process">
          {HIRING_STEPS.map((step, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-step" key={step.n}>
              <span>{step.n}</span>
              <i>↗</i>
              <h3>{step.t}</h3>
              <p>{step.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHAT WE LOOK FOR — service cards, reusing the real chooseus values */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">What we look for</div>
              <h2>The qualities that<br />define this team.</h2>
            </div>
            <p>The same standards we hold ourselves to on every project.</p>
          </Reveal>
          <div className="ea-cards">
            {QUALITIES.map((item, i) => (
              <Reveal as="article" i={(i % 4) + 1} reduceMotion={reduceMotion} key={item.title} className={`ea-card ${item.featured ? "ea-featured" : ""}`}>
                <div className="ea-icon"><item.Icon aria-hidden="true" /></div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE LOOK FOR IN A RESUME + FACTS */}
      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Beyond the resume</div>
              <h2>What we weigh<br />before an offer.</h2>
            </div>
            <p>Technical skill matters, but these questions decide the close calls.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>What we look for</h3>
              {LOOK_FOR_CRITERIA.map((row) => (
                <div key={row.a}><span>{row.a}</span><small>{row.b}</small></div>
              ))}
            </Reveal>
            <div className="ea-metrics">
              {FACTS.map((m, i) => (
                <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}>
                  <strong>{m.a}</strong>
                  <span>{m.b}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* A TYPICAL HIRING JOURNEY — case-study-shaped spotlight */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">What to expect</div>
            <h2>The same four stages,<br />every time.</h2>
          </div>
          <p>No shortcuts for referrals, no extra hoops for anyone else.</p>
        </Reveal>
        <Reveal as="div" className="ea-case">
          <div>
            <div className="ea-eyebrow">CV → CALL → ASSESSMENT → OFFER</div>
            <h3>One transparent path, start to finish.</h3>
            <p>From your first CV to a final conversation with our CEO, every candidate goes through the same process — so you always know what stage you're at, and what comes next.</p>
            <button className="ea-text-link" onClick={() => navigate("/career")}>View open roles</button>
          </div>
          <div className="ea-results">
            {HIRING_RESULTS.map((r) => (
              <div key={r.a}><b>{r.a}</b><span>{r.b}</span></div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* WHO THRIVES HERE */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Who thrives here</div>
            <h2>Built for people<br />who ship.</h2>
          </div>
          <p>The traits that make someone a strong fit for this team.</p>
        </Reveal>
        <div className="ea-industries">
          {WHO_THRIVES_HERE.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ea-industry" key={item.a}>
              <b>{item.a}</b>
              <p>{item.b}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Before you apply.</h2>
            <p>A couple of things candidates usually ask. See our full FAQ for more.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `team-faq-panel-${i}`;
            const buttonId = `team-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  >
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <a className="ea-text-link" href="/faq" onClick={(e) => { e.preventDefault(); navigate("/faq"); }}>See the full FAQ</a>
          </div>
        </div>
      </section>

      {/* JOIN CTA */}
      <section className="ea-cta" aria-labelledby="core-team-cta">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">Ready to join us?</div>
            <h2 id="core-team-cta">Start your career with us.</h2>
            <p>We're always looking for people who want to grow alongside a team that values their contribution.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Apply in minutes.</h3>
            <p>Submit your resume and we'll walk you through the four stages above.</p>
            <button className="ea-button ea-primary" style={{ marginTop: 12 }} onClick={() => setIsJobFormOpen(true)}>
              Submit your resume ↗
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default OurCoreTeam;
